import type { ChecklistInstance, ChecklistInstanceItem } from '@/types/settings';

export interface ChecklistBlockingIssue {
  sectionName: string;
  itemLabel: string;
  message: string;
  type: 'REQUIRED_VALUE' | 'REQUIRED_PHOTO';
}

export interface ChecklistProgress {
  done: number;
  total: number;
  blockingIssues: ChecklistBlockingIssue[];
}

function hasRequiredItemValue(item: ChecklistInstanceItem): boolean {
  if (item.type === 'PHOTO') {
    return true;
  }
  if (item.type === 'CHECKBOX') {
    return item.value === true;
  }
  if (item.value === null || item.value === undefined) {
    return false;
  }
  if (typeof item.value === 'string') {
    return item.value.trim().length > 0;
  }
  return true;
}

export function getRequiredPhotoCount(item: ChecklistInstanceItem): number {
  if (item.type !== 'PHOTO' && !item.photoRequired) {
    return 0;
  }

  const configuredMin = Number(item.config?.minPhotos ?? item.minPhotos ?? 1);
  if (!Number.isFinite(configuredMin) || configuredMin < 1) {
    return 1;
  }
  return configuredMin;
}

export function isChecklistItemComplete(item: ChecklistInstanceItem): boolean {
  const requiredPhotoCount = getRequiredPhotoCount(item);
  const photoOk = requiredPhotoCount === 0 || (item.photos?.length ?? 0) >= requiredPhotoCount;

  if (item.type === 'CHECKBOX') {
    return item.value === true && photoOk;
  }
  if (item.type === 'PHOTO') {
    return photoOk;
  }
  if (item.value === null || item.value === undefined) {
    return false;
  }
  if (typeof item.value === 'string' && item.value.trim().length === 0) {
    return false;
  }
  return photoOk;
}

export function getChecklistBlockingIssues(instance: ChecklistInstance): ChecklistBlockingIssue[] {
  const issues: ChecklistBlockingIssue[] = [];

  for (const section of instance.sections) {
    for (const item of section.items) {
      if (item.required && !hasRequiredItemValue(item)) {
        issues.push({
          sectionName: section.name,
          itemLabel: item.label,
          message: `${item.label} in ${section.name} is required`,
          type: 'REQUIRED_VALUE',
        });
      }

      const requiredPhotoCount = getRequiredPhotoCount(item);
      if (requiredPhotoCount > 0 && (item.photos?.length ?? 0) < requiredPhotoCount) {
        issues.push({
          sectionName: section.name,
          itemLabel: item.label,
          message: `${item.label} in ${section.name} requires ${requiredPhotoCount} photo(s)`,
          type: 'REQUIRED_PHOTO',
        });
      }
    }
  }

  return issues;
}

export function computeChecklistProgress(instance: ChecklistInstance): ChecklistProgress {
  let done = 0;
  let total = 0;

  for (const section of instance.sections) {
    for (const item of section.items) {
      total += 1;
      if (isChecklistItemComplete(item)) {
        done += 1;
      }
    }
  }

  return {
    done,
    total,
    blockingIssues: getChecklistBlockingIssues(instance),
  };
}
